/**
 * Capacity Help Content
 * 
 * Feature: 136-dashboard-alignment (T046)
 * Pages: /capacity-metrics, /capacity/executive, /capacity/list, /capacity/dependencies
 * Constitution: v1.12.0
 */

import type { PageHelpContent } from '../../components/ui/PageHelpPanel';

export const capacityHelp: PageHelpContent = {
  pageTitle: 'Capacity Management',
  purpose: 'Monitor, analyze, and manage Fabric capacity utilization across all four capacity types defined in the Constitution.',
  sections: [
    {
      title: 'Capacity Types',
      paragraphs: [
        'The system tracks four distinct capacity types, each with different SLA requirements and management patterns.',
      ],
      concepts: [
        { term: 'Platform (Type 1)', definition: 'Core infrastructure capacity managed by the platform team. Failures trigger CRITICAL alerts due to broad blast radius.' },
        { term: 'Reference (Type 2)', definition: 'Template capacity used as a baseline for standardized client deployments. Changes propagate to downstream capacities.' },
        { term: 'ClientDedicated (Type 3)', definition: 'Capacity exclusively allocated to a single client. Isolated failures with limited blast radius.' },
        { term: 'MultiTenant (Type 4)', definition: 'Shared capacity serving multiple clients. Failures trigger CRITICAL alerts due to multi-client impact.' },
      ],
    },
    {
      title: 'Metrics & Utilization',
      concepts: [
        { term: 'CPU Utilization', definition: 'Average CPU usage across all workloads running on the capacity. Target: below 80%.' },
        { term: 'Memory Utilization', definition: 'Average memory consumption. High memory can indicate resource contention.' },
        { term: 'Health Score', definition: 'Composite score (0-100) considering CPU, memory, error rates, and SLA compliance.' },
        { term: 'Trend', definition: 'Direction of health changes: Improving (↑), Stable (→), or Degrading (↓).' },
      ],
    },
    {
      title: 'Executive Dashboard',
      paragraphs: [
        'The executive view provides high-level KPIs for leadership: total capacity count, overall health score, SLA compliance percentage, and capacity utilization trends across all types.',
      ],
    },
    {
      title: 'Dependency Mapping',
      paragraphs: [
        'The dependency view shows how capacities relate to each other and to downstream resources (workspaces, lakehouses, warehouses). This is critical for blast radius analysis — understanding the impact of a capacity failure.',
      ],
      concepts: [
        { term: 'Blast Radius', definition: 'The scope of impact when a capacity fails. Platform and MultiTenant capacities have the largest blast radius.' },
        { term: 'SLA Impact', definition: 'Calculated propagation of SLA violations from capacity issues to affected clients and resources.' },
      ],
    },
    {
      title: 'How To',
      steps: [
        { step: 1, instruction: 'Navigate to the Capacity List page to see all capacities grouped by type (Platform, Reference, ClientDedicated, MultiTenant).' },
        { step: 2, instruction: 'Review the Health Score column to identify any capacities scoring below 80 that may need attention.' },
        { step: 3, instruction: 'Click a capacity row to open its detail view and check CPU and memory utilization trends.' },
        { step: 4, instruction: 'Switch to the Executive Dashboard tab for a high-level summary of capacity KPIs and SLA compliance.' },
        { step: 5, instruction: 'Use the Dependency Mapping view to assess blast radius before performing maintenance on a capacity.' },
      ],
    },
    {
      title: 'Related Pages',
      links: [
        { label: 'Dashboard', href: '/dashboard' },
        { label: 'Workspaces', href: '/workspaces' },
        { label: 'Infrastructure Health', href: '/infrastructure/health' },
      ],
    },
  ],
};

export default capacityHelp;
